import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Ticket, X } from 'lucide-react';
import {
  PartnerTicketService,
  RSA_TICKET_SERVICE_TYPES,
} from '../../services/partnerTicketService';

const emptyForm = {
  id: null,
  name: '',
  description: '',
  price_inr: '',
  credits_inr: '',
  tickets_count: '1',
  allowed_service_types: [],
  active: true,
  sort_order: '0',
};

const field =
  'mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20';

const PartnerRsaTickets = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('products');
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['partnerTicketProducts'],
    queryFn: () => PartnerTicketService.listProducts(),
  });

  const { data: purchases = [], isLoading: purchasesLoading } = useQuery({
    queryKey: ['partnerTicketPurchases'],
    queryFn: () => PartnerTicketService.listPurchases(80),
    enabled: tab === 'purchases',
  });

  const saveMutation = useMutation({
    mutationFn: (payload) => PartnerTicketService.upsertProduct(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partnerTicketProducts'] });
      setShowForm(false);
      setForm(emptyForm);
      setError('');
    },
    onError: (err) => setError(err?.message || 'Save failed'),
  });

  const sortedProducts = useMemo(
    () =>
      [...products].sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0) || String(a.name).localeCompare(String(b.name)),
      ),
    [products],
  );

  const openCreate = () => {
    setForm(emptyForm);
    setError('');
    setShowForm(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      name: row.name || '',
      description: row.description || '',
      price_inr: String(row.price_inr ?? ''),
      credits_inr: String(row.credits_inr ?? row.price_inr ?? ''),
      tickets_count: String(row.tickets_count ?? '1'),
      allowed_service_types: Array.isArray(row.allowed_service_types) ? [...row.allowed_service_types] : [],
      active: row.active !== false,
      sort_order: String(row.sort_order ?? 0),
    });
    setError('');
    setShowForm(true);
  };

  const toggleService = (service) => {
    setForm((prev) => {
      const set = new Set(prev.allowed_service_types || []);
      if (set.has(service)) set.delete(service);
      else set.add(service);
      return { ...prev, allowed_service_types: [...set] };
    });
  };

  const onSave = (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) {
      setError('Name required');
      return;
    }
    if (!form.allowed_service_types.length) {
      setError('Select at least one service type');
      return;
    }
    const price = Number(form.price_inr);
    const credits =
      form.credits_inr === '' || form.credits_inr == null
        ? price
        : Number(form.credits_inr);
    saveMutation.mutate({
      id: form.id,
      name: form.name.trim(),
      description: form.description.trim(),
      price_inr: price,
      credits_inr: credits,
      tickets_count: Number(form.tickets_count),
      allowed_service_types: form.allowed_service_types,
      active: form.active,
      sort_order: Number(form.sort_order) || 0,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Ticket className="h-6 w-6 text-primary-600" />
            Partner RSA Tickets
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            1 ticket = 1 successful RSA. Pack price = credits. Checkout adds 18% GST (like membership).
            Coupons: set coupon <code className="text-xs">applies_to_plan_slug</code> to{' '}
            <code className="text-xs">partner-ticket</code> or <code className="text-xs">all</code>.
          </p>
        </div>
        {tab === 'products' ? (
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            New pack
          </button>
        ) : null}
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        {[
          { id: 'products', label: 'Catalog' },
          { id: 'purchases', label: 'Purchases' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
              tab === t.id
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'products' ? (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading…
            </div>
          ) : sortedProducts.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500">
              No ticket packs yet. Create one for mechanics to buy.
            </div>
          ) : (
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Price</th>
                  <th className="px-4 py-3">Credits</th>
                  <th className="px-4 py-3">Tickets</th>
                  <th className="px-4 py-3">Services</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedProducts.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{row.name}</div>
                      {row.description ? (
                        <div className="text-xs text-slate-500">{row.description}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 font-medium">₹{Number(row.price_inr).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3">
                      {Number(row.credits_inr ?? row.price_inr ?? 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3">{row.tickets_count}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(row.allowed_service_types || []).map((s) => (
                          <span
                            key={s}
                            className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          row.active !== false
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {row.active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-white"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          {purchasesLoading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading…
            </div>
          ) : purchases.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500">No purchases yet.</div>
          ) : (
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Mechanic</th>
                  <th className="px-4 py-3">Pack</th>
                  <th className="px-4 py-3">Tickets</th>
                  <th className="px-4 py-3">Credits</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {purchases.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-3 text-slate-600">
                      {row.paid_at || row.created_at
                        ? new Date(row.paid_at || row.created_at).toLocaleString('en-IN')
                        : '—'}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {row.mechanic_name || row.mechanic_id?.slice?.(0, 8) || '—'}
                    </td>
                    <td className="px-4 py-3">{row.product_name || '—'}</td>
                    <td className="px-4 py-3">{row.tickets_credited}</td>
                    <td className="px-4 py-3">
                      {Number(row.credits_credited ?? row.amount_inr ?? 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3">₹{Number(row.amount_inr || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          row.status === 'paid'
                            ? 'bg-emerald-50 text-emerald-700'
                            : row.status === 'failed'
                              ? 'bg-red-50 text-red-700'
                              : 'bg-amber-50 text-amber-700'
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showForm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h2 className="text-lg font-bold text-slate-900">
                {form.id ? 'Edit ticket pack' : 'New ticket pack'}
              </h2>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={onSave} className="space-y-4 px-5 py-4">
              <div>
                <label className="text-xs font-semibold text-slate-600">Name</label>
                <input
                  className={field}
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Towing 10-pack"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Description</label>
                <input
                  className={field}
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600">Price (INR)</label>
                  <input
                    className={field}
                    type="number"
                    min="1"
                    step="1"
                    value={form.price_inr}
                    onChange={(e) => {
                      const price = e.target.value;
                      setForm((p) => {
                        const creditsFollowsPrice =
                          !p.credits_inr || p.credits_inr === p.price_inr || p.credits_inr === '';
                        return {
                          ...p,
                          price_inr: price,
                          credits_inr: creditsFollowsPrice ? price : p.credits_inr,
                        };
                      });
                    }}
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Credits</label>
                  <input
                    className={field}
                    type="number"
                    min="0"
                    step="1"
                    value={form.credits_inr}
                    onChange={(e) => setForm((p) => ({ ...p, credits_inr: e.target.value }))}
                    placeholder="= price"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">Default = price. Used for cancel penalty.</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Tickets in pack</label>
                  <input
                    className={field}
                    type="number"
                    min="1"
                    step="1"
                    value={form.tickets_count}
                    onChange={(e) => setForm((p) => ({ ...p, tickets_count: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Allowed service types</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {RSA_TICKET_SERVICE_TYPES.map((s) => {
                    const on = form.allowed_service_types.includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleService(s)}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${
                          on
                            ? 'border-primary-600 bg-primary-50 text-primary-700'
                            : 'border-slate-200 bg-white text-slate-600'
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600">Sort order</label>
                  <input
                    className={field}
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm((p) => ({ ...p, sort_order: e.target.value }))}
                  />
                </div>
                <label className="flex items-end gap-2 pb-2 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  Active
                </label>
              </div>
              {error ? <p className="text-sm font-medium text-red-600">{error}</p> : null}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PartnerRsaTickets;
